<template>
  <div class="bg-white p-8 rounded-[16px] shadow-lg w-[520px] overflow-auto scrollbar max-h-full">
    <!-- Modal Header -->
    <div class="flex justify-between items-center mb-4">
      <h2 class="text-[22px] font-semibold text-dark">{{ title }}</h2>
      <button @click="modal.rejectModal('cancel')" class="text-grey hover:text-dark">
        <img src="@/assets/icons/close.svg" alt="close" class="h-6 w-6" />
      </button>
    </div>
    <form @submit="onSubmit" class="space-y-4">
      <FormField v-slot="{ componentField }" name="sku">
        <FormItem>
          <FormLabel>Артикул</FormLabel>
          <FormControl>
            <Input type="text" placeholder="Введите артикул" v-bind="componentField" />
          </FormControl>
          <FormMessage />
        </FormItem>
      </FormField>
      <FormField v-slot="{ componentField }" name="name">
        <FormItem>
          <FormLabel>Название</FormLabel>
          <FormControl>
            <Input type="text" placeholder="Введите название" v-bind="componentField" />
          </FormControl>
          <FormMessage />
        </FormItem>
      </FormField>
      <FormField v-slot="{ componentField }" name="description">
        <FormItem>
          <FormLabel>Описание</FormLabel>
          <FormControl>
            <Input type="text" placeholder="Введите описание" v-bind="componentField" />
          </FormControl>
          <FormMessage />
        </FormItem>
      </FormField>
      <FormField v-slot="{ componentField }" name="price">
        <FormItem>
          <FormLabel>Цена</FormLabel>
          <NumberField
            :model-value="componentField.modelValue"
            @update:model-value="(v) => {
              if (v) {
                form.setFieldValue('price', v)
              }
              else {
                form.setFieldValue('price', undefined)
              }
          }"
            :min="1"
            :format-options="{
              minimumFractionDigits: 2,
            }"
          >
            <NumberFieldContent>
              <NumberFieldInput />
              <span class="absolute end-0 inset-y-0 flex items-center justify-center px-2 border border-input text-[12px] rounded-[6px] w-[50px]">
                {{ getCurrency }}
              </span>
            </NumberFieldContent>
          </NumberField>
          <FormMessage />
        </FormItem>
      </FormField>
      <div class="flex items-top gap-2">
        <FormField v-slot="{ componentField }" name="unit_quantity">
          <FormItem class="flex-1">
            <FormLabel>Количество</FormLabel>
            <FormControl>
              <NumberField
                :model-value="componentField.modelValue"
                @update:model-value="(v) => {
                  if (v) {
                    form.setFieldValue('unit_quantity', v)
                  }
                  else {
                    form.setFieldValue('unit_quantity', undefined)
                  }
                }"
                :min="unitBaseRatio"
                :format-options="unitFormatOptions"
              >
                <NumberFieldContent>
                  <NumberFieldInput />
                </NumberFieldContent>
              </NumberField>
            </FormControl>
            <FormMessage />
          </FormItem>
        </FormField>

        <FormField v-slot="{ componentField }" name="unit_id">
          <FormItem class="flex-1">
            <FormLabel>Ед. изм.</FormLabel>
            <FormControl>
              <Select v-bind="componentField">
                <SelectTrigger>
                  <SelectValue placeholder="Тип" />
                </SelectTrigger>
                <SelectContent>
                  <SelectGroup>
                    <SelectLabel>Единицы</SelectLabel>
                    <SelectItem v-for="unit in dictionaryStore.dictionary.product_units" :key="unit.id" :value="unit.id">
                      {{ unit.name }}
                    </SelectItem>
                  </SelectGroup>
                </SelectContent>
              </Select>
            </FormControl>
            <FormMessage />
          </FormItem>
        </FormField>
      </div>
      <div class="flex items-top gap-2">
        <FormField v-slot="{ componentField }" name="color">
        <FormItem class="flex-1">
          <FormLabel>Цвет</FormLabel>
          <FormControl>
            <Input type="color" placeholder="Выберите цвет" v-bind="componentField" />
          </FormControl>
          <FormMessage />
        </FormItem>
      </FormField>
      <FormField v-slot="{ componentField }" name="order">
        <FormItem class="flex-1">  
          <FormLabel>Порядок</FormLabel>
          <FormControl>
            <Input type="number" placeholder="Введите порядок" v-bind="componentField" />
          </FormControl>
          <FormMessage />
        </FormItem>
      </FormField>
      </div>

      <div class="flex items-center gap-6">
        <FormField v-slot="{ componentField }" name="is_service_fee">
          <FormItem class="flex flex-row items-end gap-2">
            <FormControl>
              <Checkbox v-bind="componentField" :checked="componentField.modelValue" />
            </FormControl>
            <FormLabel class="!mb-0">Сервисный продукт</FormLabel>
          </FormItem>
        </FormField>
        <FormField v-slot="{ componentField }" name="is_active">
          <FormItem class="flex flex-row items-end gap-2">
            <FormControl>
              <Checkbox v-bind="componentField" :checked="componentField.modelValue" />
            </FormControl>
            <FormLabel class="!mb-0">Активен</FormLabel>
          </FormItem>
        </FormField>
      </div>

      <div class="flex justify-end gap-4">
        <Button v-if="props.product" variant="destructive" type="button" :disabled="isDeleting" class="" @click="handleDeleteProduct">
          <Loader2 v-if="isDeleting" class="w-4 h-4 mr-2 animate-spin" />
          {{ isDeleting ? 'Удаление...' : 'Удалить' }}
        </Button>

        <Button type="submit" :disabled="isLoading" class="bg-primary h-full text-white font-medium rounded-[12px] text-[15px] hover:bg-[#40a9ff] transition-colors">
          <Loader2 v-if="isLoading" class="w-4 h-4 mr-2 animate-spin" />
          {{ isLoading ? 'Сохранение...' : 'Сохранить' }}
        </Button>
      </div>
    </form>
  </div>
</template>

<script setup>
import { ref, reactive, toRaw, computed } from 'vue'
import { useModalStore } from '@/store/modal'
import { useToast } from '@/components/ui/toast'
import { Input } from '@/components/ui/input'
import { Checkbox } from '@/components/ui/checkbox'
import { Select, SelectTrigger, SelectValue, SelectContent, SelectGroup, SelectLabel, SelectItem } from '@/components/ui/select'
import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import {
  NumberField,
  NumberFieldContent,
  NumberFieldDecrement,
  NumberFieldIncrement,
  NumberFieldInput,
} from '@/components/ui/number-field'
import { Button } from '@/components/ui/button'
import { Loader2 } from 'lucide-vue-next'
import {useForm, configure, defineRule} from 'vee-validate'
import { toTypedSchema } from '@vee-validate/zod'
import * as z from 'zod'
import {useSettingsStore} from '@/store/settings'
import { useDictionaryStore } from '@/store/dictionary'
import { createProduct, updateProduct, deleteProduct } from '@/api/core'

const settingsStore = useSettingsStore()
const dictionaryStore = useDictionaryStore()
const { toast } = useToast()
const modal = useModalStore()
const props = modal.modalProps
const isLoading = ref(false)
const isDeleting = ref(false)
const formSchema = toTypedSchema(z.object({
  name: z.string().min(2, 'Минимальная длина 2 символа').max(50, 'Максимальная длина 50 символов'),
  description: z.string({ required_error: 'Поле обязательно' }),
  price: z.number().min(1, 'Минимальное значение 1').max(1000000, 'Максимальное значение 1000000'),
  sku: z.string({ required_error: 'Поле обязательно' })
    .regex(/^[A-Z0-9-]+$/, { message: 'Допустимы только английские заглавные буквы, цифры и тире' })
    .min(2, 'Минимальная длина 2 символа')
    .max(32, 'Максимальная длина 32 символов'),
  color: z.string().min(2, 'Минимальная длина 2 символа').max(50, 'Максимальная длина 50 символов'),
  order: z.number().min(0, 'Минимальное значение 0').max(100, 'Максимальное значение 100'),
  unit_quantity: z.number().min(0, 'Минимальное значение 0'),
  unit_id: z.string({
      required_error: 'Поле обязательно',
      invalid_type_error: 'Нужно выбрать',
    }),
  is_service_fee: z.boolean().default(false),
  is_active: z.boolean().default(true),
}))

/** Props
 * category_id
 * product_id?
 */
const form = useForm({
  validationSchema: formSchema,
  initialValues: {
    name: props.product?.name ?? '',
    description: props.product?.description ?? '',
    price: parseFloat(props.product?.price ?? 0),
    sku: props.product?.sku ?? '',
    color: props.product?.color ? '#' + props.product.color : '#ffffff',
    order: props.product?.order ?? 0,
    unit_quantity: parseFloat(props.product?.unit_quantity ?? 1),
    unit_id: props.product?.unit_id ?? null,
    is_service_fee: props.product?.is_service_fee ?? false,
    is_active: props.product?.is_active ?? true,
  },
})

const handleDeleteProduct = () => {
  isDeleting.value = true
  deleteProduct(props.product_id).then((response) => {
    console.log(response)
    if (response.status === 200 || response.status === 204) { 
      toast({
        title: 'Успешно',
        description: 'Продукт удален',
      })
      modal.resolveModal(true)
    } else {
      toast({
        title: 'Ошибка',
        description: response.data.message || 'Не удалось удалить продукт',
        variant: 'destructive',
      })
    }
    isDeleting.value = false
  }).catch((error) => {
    console.log(error)
    toast({
      title: 'Ошибка',
      description: 'Не удалось удалить продукт',
      variant: 'destructive',
    })
    isDeleting.value = false
  })
}

const title = computed(() => {
    if (props.product) {
        return 'Редактирование продукта'
    } else {
        return 'Создание продукта'
    }
})

const getCurrency = computed(() => {
  return settingsStore.currency.code
})

const currentUnit = computed(() => {
  return dictionaryStore.dictionary.product_units?.find((u) => u.id === form.values.unit_id)
})

const unitBaseRatio = computed(() => currentUnit.value?.base_ratio ?? 1)

const unitFormatOptions = computed(() => {
  const ratioString = unitBaseRatio.value.toString()
  const decimalPart = ratioString.includes('.') ? ratioString.split('.')[1] : ''
  const digits = decimalPart.length
  return {
    minimumFractionDigits: parseFloat(digits),
    maximumFractionDigits: parseFloat(digits),
  }
})

const onSubmit = form.handleSubmit((values) => {
  isLoading.value = true
  console.log('Form submitted!', values, props.country_id, props.category_id ?? null)
  let data = {
    name: values.name,
    color: values.color.substring(1),
    sort_order: values.order,
    category_id: props.category_id,
    unit_id: values.unit_id,
    unit_quantity: values.unit_quantity,
    price: values.price,
    sku: values.sku,
    description: values.description,
    is_service_fee: values.is_service_fee,
    is_active: values.is_active,
  }

  if(props.product) { //UPDATE

    console.log('update',props)
    updateProduct(props.product.id, data).then((response) => {
      console.log(response)

      if (response.status === 200) { 
        toast({
          title: 'Успешно',
          description: 'Продукт обновлен',
        })
        modal.resolveModal(true)
      } else {
        toast({
          title: 'Ошибка',
          description: response.data.message || 'Не удалось обновить продукт',
          variant: 'destructive',
        })
      }
      isLoading.value = false
    }).catch((error) => {
      console.log(error)
      toast({
        title: 'Ошибка',
        description: 'Не удалось обновить продукт',
        variant: 'destructive',
      })
      isLoading.value = false
    })
  } else { //CREATE
    createProduct(data).then((response) => {
      console.log(response)

      if (response.status === 200 || response.status === 201) { 
        toast({
          title: 'Успешно',
          description: 'Продукт создан',
        })
        modal.resolveModal(true)
      } else {
        toast({
          title: 'Ошибка',
          description: response.data.message || 'Не удалось создать продукт',
          variant: 'destructive',
        })
      }
      isLoading.value = false
    }).catch((error) => {
      console.log(error)
      toast({
        title: 'Ошибка',
        description: 'Не удалось создать продукт',
        variant: 'destructive',
      })
      isLoading.value = false
    })
  }
})



</script>

<style scoped>
/* Additional styling if needed */
</style>
